import React from 'react';
import { CarouselCard, WhatsAppButton, WhatsAppTemplate } from '../types';

/** A template as the lead sees it — one bubble, house tokens, no phone
 *  chrome (a real chat screen is not simulated: §3.22). A row that fills a
 *  shape renders through its parent with its params applied. */

const BLANK = /\{\{(\d+)\}\}/g;
export const fill = (text: string | null | undefined, values: string[]): string =>
  (text || '').replace(BLANK, (m, n) => values[Number(n) - 1] || m);

export interface Rendered {
  templateType: WhatsAppTemplate['template_type'];
  headerText?: string | null;
  headerMediaUrl?: string | null;
  body: string;
  buttons: WhatsAppButton[];
  cards: CarouselCard[];
  footer?: string | null;
}

export const renderTemplate = (t: WhatsAppTemplate, parent?: WhatsAppTemplate): Rendered | null => {
  const shape = t.parent_id ? parent : t;
  if (!shape) return null;
  const values = t.parent_id ? (t.params.body_params || []) : (shape.body_variables || []);
  return {
    templateType: shape.template_type,
    headerText: shape.header_text ? fill(shape.header_text, values) : null,
    headerMediaUrl: (t.parent_id && t.params.header_media_url) || shape.header_media_url,
    body: fill(shape.body, values),
    buttons: shape.buttons || [],
    cards: shape.template_type === 'CAROUSEL' ? shape.carousel_cards || [] : [],
    footer: shape.footer,
  };
};

/** The picture a tile shows: header media, else the first carousel card's. */
export const thumbOf = (r: Rendered): { url: string; video: boolean } | null => {
  if (r.templateType === 'CAROUSEL') {
    const c = r.cards.find((card) => card.header_media_url);
    return c ? { url: c.header_media_url!, video: c.header_format === 'VIDEO' } : null;
  }
  return r.headerMediaUrl ? { url: r.headerMediaUrl, video: r.templateType === 'VIDEO' } : null;
};

/** "video · Book my visit, Send me details" / "carousel · 5 cards". */
export const shapeLabel = (r: Rendered): string => {
  if (r.templateType === 'CAROUSEL') return `carousel · ${r.cards.length} cards`;
  const labels = r.buttons.map((b) => b.text).filter(Boolean);
  return `${r.templateType.toLowerCase().replace('_', ' ')} · ${labels.length ? labels.join(', ') : 'no buttons'}`;
};

const Media: React.FC<{ url: string; video: boolean; className: string }> = ({ url, video, className }) =>
  video
    ? <video src={url} muted playsInline preload="metadata" className={className} />
    : <img src={url} alt="" className={className} />;

const btnCls = 'block text-center text-[#2c6479] font-semibold border-t-[1.5px] border-charcoal/10 mt-1.5 pt-1.5';

const WhatsAppPreview: React.FC<{ r: Rendered; wide?: boolean }> = ({ r, wide }) => (
  <div className={`bg-[#e7f3e2] rounded-2xl rounded-tl-sm px-3 py-2.5 text-[13px] leading-snug text-charcoal ${wide ? '' : 'max-w-[300px]'}`}>
    {r.templateType === 'CAROUSEL' ? (
      <>
        <div className="whitespace-pre-wrap break-words">{r.body}</div>
        <div className="flex gap-2 overflow-x-auto pb-1.5 mt-2" style={{ scrollbarWidth: 'thin' }}>
          {r.cards.map((c, i) => (
            <div key={i} className="flex-none w-[150px] bg-warm-white rounded-lg p-1.5 text-[11.5px]">
              <div className="h-[110px] rounded-md overflow-hidden bg-[#2b3a2e] mb-1.5">
                {c.header_media_url && <Media url={c.header_media_url} video={c.header_format === 'VIDEO'} className="w-full h-full object-cover" />}
              </div>
              {c.title && <b className="block mb-0.5">{c.title}</b>}
              <span className="text-charcoal/80">{fill(c.body, c.body_variables || []).slice(0, 90)}</span>
              {c.buttons?.[0] && <span className={`${btnCls} text-[11px]`}>{c.buttons[0].text}</span>}
            </div>
          ))}
        </div>
      </>
    ) : (
      <>
        {r.headerMediaUrl && (
          <div className="aspect-[16/10] rounded-lg overflow-hidden bg-[#2b3a2e] mb-2 relative">
            <Media url={r.headerMediaUrl} video={r.templateType === 'VIDEO'} className="w-full h-full object-cover" />
            {r.templateType === 'VIDEO' && <span className="absolute inset-0 grid place-items-center text-warm-white/90 text-[26px]">▶</span>}
          </div>
        )}
        {r.templateType === 'DOCUMENT' && !r.headerMediaUrl && <div className="text-graphite mb-1">📄 document</div>}
        {r.headerText && <b className="block mb-1">{r.headerText}</b>}
        <div className="whitespace-pre-wrap break-words">{r.body}</div>
        {r.footer && <div className="text-graphite text-[11px] mt-1.5">{r.footer}</div>}
        {r.buttons.map((b, i) => <span key={i} className={btnCls}>{b.type === 'PHONE_NUMBER' ? '📞 ' : ''}{b.text}</span>)}
      </>
    )}
  </div>
);

export default WhatsAppPreview;
